import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class DavesaValidators {
    /**
     * Check for empty (optional fields) values
     *
     * @param value
     */
    static isEmptyInputValue(value: any): boolean {
        return value == null || value.length === 0;
    }

    /**
     * Must match validator
     *
     * @param controlPath A dot-delimited string values that define the path to the control.
     * @param matchingControlPath A dot-delimited string values that define the path to the matching control.
     */
    static mustMatch(
        controlPath: string,
        matchingControlPath: string
    ): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            // Get the control and matching control
            const control = formGroup.get(controlPath);
            const matchingControl = formGroup.get(matchingControlPath);

            // Return if control or matching control doesn't exist
            if (!control || !matchingControl) {
                return null;
            }

            // Delete the mustMatch error to reset the error on the matching control
            if (matchingControl.hasError('mustMatch')) {
                delete matchingControl.errors.mustMatch;
                matchingControl.updateValueAndValidity();
            }

            // Don't validate empty values on the matching control
            // Don't validate if values are matching
            if (
                this.isEmptyInputValue(matchingControl.value) ||
                control.value === matchingControl.value
            ) {
                return null;
            }

            // Prepare the validation errors
            const errors = { mustMatch: true };

            // Set the validation error on the matching control
            matchingControl.setErrors(errors);

            // Return the errors
            return errors;
        };
    }

    /**
     * File size validator for reactive forms
     * 
     * @param maxSizeInMB Maximum file size in MB (default: 30)
     */
    static fileSize(maxSizeInMB: number = 30): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const file = control.value as File;
            
            if (!file || !(file instanceof File)) {
                return null;
            }

            const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
            
            if (file.size > maxSizeInBytes) {
                return {
                    fileSize: {
                        actualSize: file.size,
                        maxSize: maxSizeInBytes,
                        actualSizeMB: Math.round(file.size / (1024 * 1024) * 100) / 100,
                        maxSizeMB: maxSizeInMB
                    }
                };
            }

            return null;
        };
    }

    /**
     * File type validator for reactive forms
     * 
     * @param allowedTypes Array of allowed file extensions or MIME types
     */
    static fileType(allowedTypes: string[]): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const file = control.value as File;
            
            if (!file || !(file instanceof File)) {
                return null;
            }

            const fileName = file.name.toLowerCase();
            const fileType = file.type.toLowerCase();
            
            const isAllowed = allowedTypes.some(type => {
                if (type.startsWith('.')) {
                    return fileName.endsWith(type.toLowerCase());
                } else if (type.includes('/')) {
                    return fileType === type.toLowerCase();
                } else {
                    return fileName.endsWith(`.${type.toLowerCase()}`);
                }
            });

            if (!isAllowed) {
                return {
                    fileType: {
                        actualType: file.type,
                        allowedTypes: allowedTypes
                    }
                };
            }

            return null;
        };
    }

    /**
     * PDF file validator for reactive forms
     */
    static pdfFile(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const file = control.value as File;
            
            if (!file || !(file instanceof File)) {
                return null;
            }

            const isPdf = file.type === 'application/pdf' || 
                         file.type.includes('pdf') ||
                         file.name.toLowerCase().endsWith('.pdf');

            if (!isPdf) {
                return {
                    pdfFile: {
                        actualType: file.type,
                        expectedType: 'application/pdf'
                    }
                };
            }

            return null;
        };
    }

    /**
     * Standard file validation (30MB + PDF) for reactive forms
     */
    static standardFileValidation(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const sizeError = DavesaValidators.fileSize(30)(control);
            const pdfError = DavesaValidators.pdfFile()(control);

            if (sizeError) return sizeError;
            if (pdfError) return pdfError;

            return null;
        };
    }

    /**
     * Only number input validator - ensures the value contains only numeric digits (0-9)
     * Allows empty values (use with Validators.required if needed)
     * Works with both string and number input types
     * 
     * @returns ValidatorFn that returns { onlyNumberInput: true } if value contains non-numeric characters
     */
    static onlyNumberInput(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            // Allow empty values (use with Validators.required if needed)
            if (this.isEmptyInputValue(control.value)) {
                return null;
            }

            // Convert to string for validation (handles both string and number inputs)
            const stringValue = String(control.value).trim();
            
            // Check if value contains only digits (0-9)
            // This regex ensures the entire string is composed of digits only
            const isNumericOnly = /^\d+$/.test(stringValue);

            return isNumericOnly ? null : { onlyNumberInput: true };
        };
    }
}
